import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { AccordionDetails, Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { FHAccordion, FHButton, FHConfirmDialog } from '../../../../base';
import { behaviorActions, goalActions } from '../../../../../store';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import GoalSummary from './GoalSummary';
import GoalDetails from './GoalDetails';
import GoalTargets from './GoalTargets';
import GoalDescriptions from './GoalDescriptions';
import {
  FORTA_ERROR,
  FORTA_GRAY4,
  FORTA_GRAY2,
  FORTA_BLUE,
  FORTA_WHITE,
} from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';
import { GOAL_DETAILS_FIELD_IDS } from '../common/constants/general';
import UnsavedIndicator from '../../components/unsavedIndicator';

import type { TreatmentPlanResourceVersion } from '../common/constants/general';
import type {
  ITreatmentPlanGoalTarget,
  ITreatmentPlanGoal,
  IBehaviorGoalName,
} from '../../../../../utils/types';
import type { RootState } from '../../../../../store';

interface GoalItemProps {
  index: number;
  values: ITreatmentPlanGoal;
  submitting?: boolean;
  targetSaving: boolean;
  errors?: any;
  goalVersion: TreatmentPlanResourceVersion;
  onUpdateField: (key: string, value: any) => void;
  onSave: () => void;
  onDelete: () => void;
  onSaveTarget: (index: number, target: ITreatmentPlanGoalTarget) => void;
  onCancelGoalChange: () => void;
}

function GoalItem(props: GoalItemProps) {
  const {
    index,
    values: goal,
    submitting = false,
    targetSaving = false,
    errors,
    goalVersion,
    onUpdateField,
    onSave,
    onDelete,
    onSaveTarget,
    onCancelGoalChange,
  } = props;

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isTpEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals } = useSelector(
    (state: RootState) => state.goals,
  );
  const { goalNames: behaviorGoalNames } = useSelector((state: RootState) => state.behaviors);
  const goalRef = useRef(goal);
  const [goalNames, setGoalNames] = useState<IBehaviorGoalName[]>([]);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [filteredGoalNames, setFilteredGoalNames] = useState<IBehaviorGoalName[]>([]);
  const [selectedGoalNameIds, setSelectedGoalNameIds] = useState<number[]>([]);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  useEffect(() => {
    goalRef.current = goal;
  }, [goal]);

  useEffect(() => {
    const behaviorId = goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id;
    if (behaviorId === undefined) return;

    if (behaviorGoalNames?.[behaviorId]) {
      setGoalNames(behaviorGoalNames[behaviorId]);
    } else {
      const fetchGoalNames = async () => {
        const { payload } = await dispatch(
          behaviorActions.getGoalNames({
            behaviorId: goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id,
            getAccessTokenSilently,
          }),
        );

        if (!payload) {
          return;
        }

        setGoalNames(payload);
      };

      fetchGoalNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id]);

  useEffect(() => {
    setIsEditable(isTpEditable && goal?.is_active);
  }, [goal?.is_active, isTpEditable]);

  useEffect(() => {
    const existingGoalNameIds = [
      ...behaviorReductionGoals,
      ...skillAcquisitionGoals,
      ...caregiverGoals,
    ]
      .filter((g: ITreatmentPlanGoal) => g.id && g.id !== goal.id)
      .map((goal: ITreatmentPlanGoal) => goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]);

    setFilteredGoalNames(
      goalNames.filter((goalName: IBehaviorGoalName) => !existingGoalNameIds.includes(goalName.id)),
    );
    setSelectedGoalNameIds(
      existingGoalNameIds.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
    );
  }, [goal, goalNames, behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals]);

  const isValidGoal = useMemo(() => {
    if (!errors) return true;

    let isValid = true;
    Object.keys(errors).forEach((key) => {
      if (key === 'targets') {
        Object.values(errors['targets']).forEach((target: any) => {
          Object.keys(target).forEach((key) => {
            if (target[key]) {
              isValid = false;
            }
          });
        });
      } else if (errors[key]) {
        isValid = false;
      }
    });

    return isValid;
  }, [errors]);

  const handleUpdateSummaryField = (key: string, value: any) => {
    if (key === 'is_editing' && Boolean(value)) {
      dispatch(
        goalActions.persistOriginalGoal({
          goalType: goal.goal_type,
          index,
          goal,
        }),
      );
    }

    onUpdateField(key, value);
  };

  const handleAddTarget = () => {
    if (!goalRef) return;

    const goal = goalRef.current;
    onUpdateField('targets', [
      ...goal.targets,
      {
        label: '',
        instruction: '',
        is_event_duration_required: false,
        is_active: true,
        is_editing: true,
        dirty: true,
      },
    ]);
  };

  const handleAddTargetWithValues = (data: any) => {
    if (!goalRef) return;

    const goal = goalRef.current;
    onUpdateField('targets', [
      ...goal.targets,
      {
        label: '',
        ...data,
      },
    ]);
  };

  const handleUpdateTargetField = (index: number, key: string, value: any) => {
    if (!goalRef) return;

    const goal = goalRef.current;
    const dirty =
      key === 'is_active' ||
      key === 'is_editing' ||
      key === 'client_progress_expanded' ||
      key === 'expanded'
        ? (goal.targets[index] && goal.targets[index]['dirty']) || false
        : (goal.targets[index] && goal.targets[index].dirty) ||
          Object.entries(goal.targets[index] ?? {}).find(([targetKey]) => targetKey === key)?.[1] !==
            value;

    onUpdateField(
      'targets',
      goal.targets.map((target, i) => (index === i ? { ...target, [key]: value, dirty } : target)),
    );
  };

  const handleDeleteTarget = async (index: number) => {
    if (!treatmentPlan?.id || !goal || !goal.targets) return;
    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    const activeTargets = targets.filter((target) => target.is_active);
    if (targets[index]?.is_active && activeTargets.length <= 1) {
      toast.error('Goal should have at least one active target.');
      return;
    }

    if (Object.hasOwn(targets[index], 'id')) {
      const res = await dispatch(
        goalActions.removeGoalTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
    }

    onUpdateField(
      'targets',
      targets.filter((_, i) => i !== index),
    );
  };

  const handleSaveTarget = (index: number, target: ITreatmentPlanGoalTarget) => {
    if (!goal || !goal.targets) return;
    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    onSaveTarget(index, {
      ...target,
    });
  };

  const handleUpdateTargetActivation = async (index: number, reason: string) => {
    if (
      !treatmentPlan ||
      !goal ||
      !goal?.targets ||
      !(goal?.targets[index] != null && Object.hasOwn(goal?.targets[index], 'id'))
    )
      return;

    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    if (reason.trim().length < 2) {
      toast.error('Reason requires at least 2 characters.');
      return;
    }

    if (targets[index].is_active) {
      const res = await dispatch(
        goalActions.deactivateTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) {
        onUpdateField(
          'targets',
          targets.map((target, i) => (index === i ? { ...target, is_active: true } : target)),
        );
        return;
      }
      toast.success('Target deactivated.');
    } else {
      const res = await dispatch(
        goalActions.activateTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) {
        onUpdateField(
          'targets',
          targets.map((target, i) => (index === i ? { ...target, is_active: false } : target)),
        );
        return;
      }
      const toastId = 'targetActivatedToast';
      toast.success('Target activated.', { toastId });
    }

    onUpdateField(
      'targets',
      targets.map((target, i) =>
        index === i ? { ...target, is_active: !target.is_active, reason } : target,
      ),
    );
  };

  const handleCancelTargetChange = (index: number, prevTarget: ITreatmentPlanGoalTarget | null) => {
    if (!goalRef) return;

    const goal = goalRef.current;
    onUpdateField(
      'targets',
      prevTarget
        ? goal.targets.map((target, i) => (index === i ? prevTarget : target))
        : goal.targets.filter((_, i) => index !== i),
    );
  };

  const handleCancelChange = () => {
    onUpdateField('is_editing', false);
    setShowConfirm(false);
    onCancelGoalChange();
  };

  const hasUnsavedChanges =
    (goal?.dirty && goal?.is_editing) || goal?.targets?.some((target) => target.dirty);

  return (
    <FHAccordion
      colorTheme={Themes.Primary}
      defaultExpanded={false}
      expanded={Boolean(goal?.expanded)}
      hasErrors={!isValidGoal}
      hasWarnings={hasUnsavedChanges}
    >
      <Box sx={{ position: 'relative' }}>
        {hasUnsavedChanges && <UnsavedIndicator />}
        <GoalSummary
          index={index}
          goal={goal}
          submitting={submitting}
          onUpdateField={handleUpdateSummaryField}
          onDelete={onDelete}
        />
      </Box>
      {goal?.expanded ? (
        <AccordionDetails sx={{ px: 3 }}>
          {goal?.is_editing && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <FHButton
                data-testid="cancelGoalButtonTop"
                sx={{
                  backgroundColor: FORTA_WHITE,
                  border: `1px solid ${FORTA_BLUE.light2}`,
                  color: FORTA_GRAY4,
                  mr: 3,
                  '&:hover': {
                    backgroundColor: FORTA_WHITE,
                    border: `1px solid ${FORTA_BLUE.light2}`,
                    color: FORTA_GRAY4,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: FORTA_WHITE,
                    color: FORTA_GRAY2,
                  },
                }}
                disabled={!isEditable || submitting}
                onClick={() => setShowConfirm(true)}
              >
                Cancel
              </FHButton>
              <Box sx={{ position: 'relative' }}>
                {goal?.dirty && <UnsavedIndicator />}
                <FHButton
                  data-testid="saveGoalButtonTop"
                  colorTheme={Themes.Primary}
                  disabled={!isEditable || submitting}
                  onClick={onSave}
                  error={goal?.dirty}
                >
                  Save Goal
                </FHButton>
              </Box>
            </Box>
          )}
          {goal?.is_editing && (
            <Typography
              color={!isValidGoal ? FORTA_ERROR : FORTA_BLUE.light1}
              mb={3}
              align="left"
              fontWeight={700}
            >
              Attention: All fields with * are required to be filled
            </Typography>
          )}
          {treatmentPlan?.id != null ? (
            <>
              <GoalDetails
                goalIndex={index}
                goal={goal}
                selectedGoalNameIds={selectedGoalNameIds}
                treatmentPlanId={treatmentPlan.id}
                goalNames={filteredGoalNames}
                errors={errors}
                onUpdateField={onUpdateField}
              />
              <GoalDescriptions
                treatmentPlanId={treatmentPlan.id}
                goalIndex={index}
                goal={goal}
                goalVersion={goalVersion}
                errors={errors}
                onUpdateField={onUpdateField}
              />
            </>
          ) : (
            <Typography color={FORTA_ERROR} mb={3}>
              Goal details are unavailable until the treatment plan loads.
            </Typography>
          )}
          <GoalTargets
            goal={goal}
            targetSaving={targetSaving}
            onAddTarget={() => {
              // To keep the text field data on blur
              setTimeout(() => {
                handleAddTarget();
              });
            }}
            onUpdateField={handleUpdateTargetField}
            onAddTargetWithValues={handleAddTargetWithValues}
            onSaveTarget={handleSaveTarget}
            onDeleteTarget={handleDeleteTarget}
            onUpdateTargetActivation={handleUpdateTargetActivation}
            onCancelTargetChange={handleCancelTargetChange}
          />
          {goal?.is_editing && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 3 }}>
              <FHButton
                data-testid="cancelGoalButton"
                sx={{
                  backgroundColor: FORTA_WHITE,
                  border: `1px solid ${FORTA_BLUE.light2}`,
                  color: FORTA_GRAY4,
                  mr: 3,
                  height: 48,
                  '&:hover': {
                    backgroundColor: FORTA_WHITE,
                    border: `1px solid ${FORTA_BLUE.light2}`,
                    color: FORTA_GRAY4,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: FORTA_WHITE,
                    color: FORTA_GRAY2,
                  },
                }}
                disabled={!isEditable || submitting}
                onClick={() => setShowConfirm(true)}
              >
                Cancel
              </FHButton>
              <Box sx={{ position: 'relative' }}>
                {goal?.dirty && <UnsavedIndicator right="-20px" top="-15px" />}
                <FHButton
                  data-testid="saveGoalButton"
                  sx={{
                    height: 48,
                  }}
                  colorTheme={Themes.Primary}
                  disabled={!isEditable || submitting}
                  onClick={onSave}
                  error={goal?.dirty}
                >
                  Save Goal
                </FHButton>
              </Box>
            </Box>
          )}
        </AccordionDetails>
      ) : null}
      <FHConfirmDialog
        open={showConfirm}
        colorTheme={Themes.Primary}
        title={'Cancel goal'}
        content={'Are you sure you want to cancel? Any new information will be lost.'}
        submittext={'Cancel'}
        canceltext={'Return to goal'}
        onClose={() => setShowConfirm(false)}
        onSubmit={handleCancelChange}
      />
    </FHAccordion>
  );
}

export default GoalItem;
